import { useCallback, useEffect, useRef } from 'react';
import {
  Button,
  Flex,
  FormControl,
  InputGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { SelectField } from '@/components/SelectField';
import { ImageUpload } from '@/components/ImageUpload';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePutBaladaAppMutation } from '@/services/queries/balada-app';
import { PutBaladaAppRequest } from '@/services/request/balada-app';
import { BaladaApp, BaladaAppType } from '@/model';
import { useTranslation } from 'react-i18next';

export interface BaladaAppEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  item: BaladaApp;
  onDone?: () => void;
}

export const BaladaAppEditModal = ({ isOpen, onClose, item, onDone }: BaladaAppEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const putItemMutation = usePutBaladaAppMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: item.title,
      identifier: item.identifier,
      type: item.type,
      source_url: item.source_url,
      file: undefined,
    },
    resolver: zodResolver(modalFormSchema),
  });

  useEffect(() => {
    reset({
      title: item.title,
      identifier: item.identifier,
      type: item.type,
      source_url: item.source_url,
      file: undefined,
    });
  }, [item, reset]);

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
  }, [clearErrors, onClose]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PutBaladaAppRequest = {
        title: data.title,
        identifier: data.identifier,
        type: data.type as BaladaAppType,
        source_url: data.source_url,
        ...(data.file ? { image: data.file } : {}),
      };

      putItemMutation.mutate(
        { uuid: item.uuid, payload },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title:
                (resp && resp.message) ||
                t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_error'),
            });
          },
        },
      );
    },
    [item.uuid, onDone, putItemMutation, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <InputField
              withoutFloating
              label={t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_label_title')}
              {...register('title')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <InputField
              withoutFloating
              label={t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_label_identifier')}
              {...register('identifier')}
              maxLength={20}
              rootProps={{ isInvalid: !!errors?.identifier, errorMessage: errors?.identifier?.message }}
            />

            <FormControl display='flex' flexDirection='column' gap={2} isInvalid={!!errors?.type}>
              <Text fontWeight='medium'>
                {t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_label_type')}
              </Text>
              <SelectField {...register('type')}>
                <option value={BaladaAppType.OTHERS}>
                  {t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_type_others')}
                </option>
                <option value={BaladaAppType.TICKETS}>
                  {t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_type_tickets')}
                </option>
                <option value={BaladaAppType.MEMBERSHIPS}>
                  {t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_type_memberships')}
                </option>
              </SelectField>
            </FormControl>

            <InputField
              withoutFloating
              label={t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_label_url')}
              helperText={t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_help_url')}
              {...register('source_url')}
              rootProps={{ isInvalid: !!errors?.source_url, errorMessage: errors?.source_url?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_label_image')}
                imageURL={item.image_url}
                onFileChange={file => setValue('file', file)}
                helpText={t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_help_text')}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={handleClose}>
              {t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_cancel')}
            </Button>
            <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
              {t('pages.application_balada_app.components.balada_app_edit_modal.balada_app_edit_save')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
